import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Card,
  CardHeader,
  CardContent,
  Typography,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  Tooltip,
  LinearProgress,
  Alert,
  Chip,
  Divider,
} from "@mui/material";
import {
  ArrowBack,
  Add,
  Delete,
  DirectionsBoat,
  Edit,
  UploadFile,
  Download,
} from "@mui/icons-material";
import { useSnackbar } from "notistack";
import dayjs from "dayjs";

import api from "../../api/axiosInstance";
import sailingService from "../../services/sailingService";
import StatusChip from "../../components/common/StatusChip";
import PageHeader from "../../components/common/PageHeader";
import SkeletonLoader from "../../components/common/SkeletonLoader";
import AssignShipmentsDialog from "../../components/sailing/AssignShipmentsDialog";
import ConfirmDialog from "../../components/common/ConfirmDialog";
import { formatUsd } from "../../utils/currency";

// Truncates by WORD count rather than character width — a description
// like "Assorted Household Goods And Electronics" reads as "Assorted
// Household Goods And…" instead of getting cut mid-word by CSS noWrap.
const truncateWords = (text, wordLimit = 4) => {
  if (!text) return text;
  const words = text.trim().split(/\s+/);
  if (words.length <= wordLimit) return text;
  return `${words.slice(0, wordLimit).join(" ")}...`;
};

const AdminSailingDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const [sailing, setSailing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [assignOpen, setAssignOpen] = useState(false);
  const [unassignTarget, setUnassignTarget] = useState(null); // shipment to unassign
  const [unassigning, setUnassigning] = useState(false);
  const [unassignAllOpen, setUnassignAllOpen] = useState(false);
  const [unassigningAll, setUnassigningAll] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [downloadingBol, setDownloadingBol] = useState(false);
  const [uploadingBol, setUploadingBol] = useState(false);
  const [downloadingDocuments, setDownloadingDocuments] = useState(false);

  // One zip: Manifest, MBL (if one's been uploaded), every customer's HBOL,
  // and every customer's invoice — see sailingController.getSailingDocumentsZip.
  const handleDownloadDocuments = async () => {
    setDownloadingDocuments(true);
    try {
      const response = await sailingService.getDocumentsZip(id);
      const disposition = response.headers?.["content-disposition"] || "";
      const filenameMatch = disposition.match(/filename="?([^"]+)"?/);
      const filename = filenameMatch ? filenameMatch[1] : `${sailing?.sailingNumber || "sailing"}-documents.zip`;

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      // responseType "blob" means a failure response body is itself a Blob,
      // not JSON — no err.response.data.message to read here, unlike a
      // normal JSON error response.
      enqueueSnackbar("Failed to download documents", { variant: "error" });
    } finally {
      setDownloadingDocuments(false);
    }
  };

  const handleDownloadBol = async () => {
    setDownloadingBol(true);
    try {
      const { data } = await api.get(`/sailing/${id}/bol/download`);
      window.open(data.data.url, "_blank");
    } catch (err) {
      enqueueSnackbar(err.response?.data?.message || "Failed to download BOL", { variant: "error" });
    } finally {
      setDownloadingBol(false);
    }
  };

  // Just archives the PDF against this sailing — no parsing, no D/R
  // matching. The sidebar's "Upload BOL" page is the one that extracts and
  // assigns data from a BOL; this is purely "keep a copy of the file."
  const handleUploadBol = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-selecting the same file later
    if (!file) return;
    setUploadingBol(true);
    try {
      const { data } = await sailingService.uploadBol(id, file);
      enqueueSnackbar(data.message || "BOL uploaded", { variant: "success" });
      load(); // re-fetch fully populated (uploadBol's response schedule isn't)
    } catch (err) {
      enqueueSnackbar(err.response?.data?.message || "Failed to upload BOL", { variant: "error" });
    } finally {
      setUploadingBol(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await api.delete(`/sailing/${id}`);
      enqueueSnackbar(`${sailing.sailingNumber} deleted`, { variant: "success" });
      navigate("/admin/sailing");
    } catch (err) {
      enqueueSnackbar(err.response?.data?.message || "Failed to delete sailing", {
        variant: "error",
      });
      setDeleting(false);
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/sailing/${id}`);
      setSailing(data.data?.schedule || null);
    } catch {
      enqueueSnackbar("Failed to load sailing schedule", { variant: "error" });
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const handleAssigned = async () => {
    await load();
  };

  const handleUnassign = async () => {
    if (!unassignTarget) return;
    setUnassigning(true);
    try {
      await api.delete(`/sailing/${id}/unassign/${unassignTarget.shipmentId}/${unassignTarget.itemId}`);
      enqueueSnackbar(`${unassignTarget.trackingNumber || "Item"} removed from sailing`, {
        variant: "success",
      });
      setUnassignTarget(null);
      load();
    } catch (err) {
      enqueueSnackbar(err.response?.data?.message || "Failed to unassign", {
        variant: "error",
      });
    } finally {
      setUnassigning(false);
    }
  };

  const handleUnassignAll = async () => {
    setUnassigningAll(true);
    try {
      const { data } = await sailingService.unassignAllItems(id);
      enqueueSnackbar(data.message || "All items unassigned", { variant: "success" });
      setUnassignAllOpen(false);
      load();
    } catch (err) {
      enqueueSnackbar(err.response?.data?.message || "Failed to unassign items", { variant: "error" });
    } finally {
      setUnassigningAll(false);
    }
  };

  // Sailing assignment is per ITEM, not per whole shipment — `sailing.items`
  // is a roster of { shipment (populated), itemId } pairs. The specific
  // item subdocument isn't returned by populate (it lives inside the
  // shipment's own `items[]` array), so it's looked up client-side from the
  // populated shipment doc. Grouped by shipment for display, mirroring
  // AssignShipmentsDialog.
  const itemRows = useMemo(
    () =>
      (sailing?.items || [])
        .map((entry) => {
          const shipmentDoc = entry.shipment;
          if (!shipmentDoc || typeof shipmentDoc !== "object") return null;
          const itemDoc = (shipmentDoc.items || []).find(
            (i) => i._id?.toString() === entry.itemId?.toString(),
          );
          return {
            entryId: entry._id,
            shipmentId: shipmentDoc._id,
            itemId: entry.itemId,
            shipment: shipmentDoc,
            item: itemDoc,
          };
        })
        .filter(Boolean),
    [sailing],
  );

  // Weight/CBF belong to the dock receipt itself (shipment.dockReceipts[]),
  // shared by every item under it — null for items not linked to a D/R yet.
  const getItemMeasurements = (row) => {
    const dr = row.item?.dockReceiptNumber
      ? (row.shipment.dockReceipts || []).find((d) => d.dockReceiptNumber === row.item.dockReceiptNumber)
      : null;
    return {
      weight: dr?.weight ?? null,
      cbf: dr?.cbf ?? null,
    };
  };

  const shipmentGroups = useMemo(() => {
    const map = new Map();
    const order = [];
    for (const row of itemRows) {
      if (!map.has(row.shipmentId)) {
        map.set(row.shipmentId, { shipmentId: row.shipmentId, shipment: row.shipment, rows: [] });
        order.push(row.shipmentId);
      }
      map.get(row.shipmentId).rows.push(row);
    }
    return order.map((sid) => map.get(sid));
  }, [itemRows]);

  if (loading && !sailing) {
    return (
      <Box sx={{ py: 4 }}>
        <SkeletonLoader rows={6} />
      </Box>
    );
  }

  if (!sailing) return null;

  const canAssign = !["arrived", "completed"].includes(sailing.status);
  const totalValue = itemRows.reduce((sum, r) => sum + (r.item?.totalValue || 0), 0);

  // Weight/CBF live on the dock receipt, shared by every item under it (see
  // getItemMeasurements) — summing per item row would double/triple-count
  // a D/R with several items on it, so this sums once per DISTINCT dock
  // receipt number instead (D/R numbers are unique across the whole
  // sailing, same grouping rule the Manifest/HBOL use).
  const totalsByDockReceipt = new Map();
  for (const row of itemRows) {
    const drNumber = row.item?.dockReceiptNumber;
    if (!drNumber || totalsByDockReceipt.has(drNumber)) continue;
    totalsByDockReceipt.set(drNumber, getItemMeasurements(row));
  }
  let totalWeight = 0;
  let totalCbf = 0;
  for (const { weight, cbf } of totalsByDockReceipt.values()) {
    totalWeight += weight || 0;
    totalCbf += cbf || 0;
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          gap: 2,
          flexWrap: "wrap",
        }}
      >
        <Button
          startIcon={<ArrowBack />}
          variant="outlined"
          size="small"
          onClick={() => navigate("/admin/sailing")}
        >
          Back
        </Button>
        <Box sx={{ flex: 1 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              flexWrap: "wrap",
              mb: 0.25,
            }}
          >
            <Typography variant="h4" fontWeight={700}>
              {sailing.vesselName}
              {sailing.voyageNumber ? ` / ${sailing.voyageNumber}` : ""}
            </Typography>
            <StatusChip status={sailing.status} domain="sailing" />
            {sailing.preCleared && (
              <Chip
                label="Pre-Cleared"
                size="small"
                color="success"
                variant="outlined"
                sx={{ fontWeight: 600 }}
              />
            )}
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontFamily: "monospace" }}
            >
              #{sailing.sailingNumber}
            </Typography>
          </Box>
          <Typography variant="caption" color="text.secondary">
            {sailing.originPort} → {sailing.destinationPort} · Departs{" "}
            {dayjs(sailing.departureDate).format("MMM D, YYYY")} · ETA{" "}
            {dayjs(sailing.estimatedArrivalDate).format("MMM D, YYYY")}
          </Typography>
        </Box>
        {canAssign && (
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => setAssignOpen(true)}
          >
            Assign Items
          </Button>
        )}
        <Button
          variant="outlined"
          startIcon={<Download />}
          disabled={downloadingDocuments || itemRows.length === 0}
          onClick={handleDownloadDocuments}
        >
          {downloadingDocuments ? "Preparing…" : "Download Documents"}
        </Button>
        <Button
          variant="outlined"
          color="error"
          startIcon={<Delete />}
          disabled={itemRows.length > 0}
          onClick={() => setDeleteOpen(true)}
        >
          Delete
        </Button>
      </Box>

      {/* Info cards row */}
      <Box
        sx={{ display: "grid", gridTemplateColumns: { xs: "repeat(2, 1fr)", md: "repeat(3, 1fr)", lg: "repeat(6, 1fr)" }, gap: 2 }}
      >
        {[
          { label: "Items", value: `${itemRows.length} (${shipmentGroups.length} shipment${shipmentGroups.length !== 1 ? "s" : ""})` },
          { label: "Total Value", value: formatUsd(totalValue) },
          { label: "Total Weight", value: `${totalWeight.toFixed(2)} lb` },
          { label: "Total CBF", value: totalCbf.toFixed(2) },
          { label: "Container", value: sailing.containerNumber || "—" },
          {
            label: "Actual Arrival",
            value: sailing.actualArrivalDate
              ? dayjs(sailing.actualArrivalDate).format("MMM D, YYYY")
              : "Pending",
          },
        ].map((c) => (
          <Card key={c.label} sx={{ p: 2 }}>
            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
            >
              {c.label}
            </Typography>
            <Typography fontWeight={700} fontSize={20} mt={0.5}>
              {c.value}
            </Typography>
          </Card>
        ))}
      </Box>

      {/* Alert for completed sailings */}
      {!canAssign && (
        <Alert severity="info" sx={{ borderRadius: 2 }}>
          This sailing has <strong>{sailing.status}</strong>. Shipments can no
          longer be assigned or removed.
        </Alert>
      )}

      {/* BOL / Freight Charges card — always shown so "Upload BOL" is
          reachable even for a sailing with no BOL/freight fields at all yet
          (e.g. one created manually rather than from a BOL upload). Shown
          above the Assigned Items list, since it's sailing-level context an
          admin wants to see before scrolling through the item roster. */}
      {(() => {
        const hasFreightDetails =
          sailing.masterBillNumber ||
          sailing.bookingNumber ||
          sailing.totalFreightAndChargesUsd != null ||
          sailing.freightCharges?.length > 0;

        return (
          <Card>
            <CardHeader
              title={
                <Typography fontWeight={700} fontSize={14}>
                  BOL / Freight Details
                </Typography>
              }
              action={
                sailing.bolDocument ? (
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<Download fontSize="small" />}
                    onClick={handleDownloadBol}
                    disabled={downloadingBol}
                  >
                    {downloadingBol ? "Downloading…" : "Download BOL"}
                  </Button>
                ) : (
                  <Button
                    size="small"
                    variant="outlined"
                    component="label"
                    startIcon={<UploadFile fontSize="small" />}
                    disabled={uploadingBol}
                  >
                    {uploadingBol ? "Uploading…" : "Upload BOL"}
                    <input type="file" accept="application/pdf" hidden onChange={handleUploadBol} />
                  </Button>
                )
              }
              sx={{ pb: 1 }}
            />
            <CardContent sx={{ pt: 0 }}>
              {!hasFreightDetails ? (
                <Typography variant="body2" color="text.secondary">
                  No BOL has been uploaded for this sailing yet.
                </Typography>
              ) : (
                <>
                  <Box sx={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 2, mb: sailing.freightCharges?.length > 0 ? 2.5 : 0 }}>
                    {[
                      { label: "Master B/L No.", value: sailing.masterBillNumber || "—" },
                      { label: "Booking No.", value: sailing.bookingNumber || "—" },
                      { label: "Freight Terms", value: sailing.freightTerms || "—" },
                      {
                        label: "Total Freight & Charges",
                        value:
                          sailing.totalFreightAndChargesUsd != null
                            ? `$${sailing.totalFreightAndChargesUsd.toFixed(2)} USD`
                            : "—",
                      },
                    ].map((f) => (
                      <Box key={f.label}>
                        <Typography variant="caption" color="text.secondary" display="block">
                          {f.label}
                        </Typography>
                        <Typography fontWeight={600}>{f.value}</Typography>
                      </Box>
                    ))}
                  </Box>

                  {(sailing.totalFreightAndChargesCi != null || sailing.craneSurchargeAmount || sailing.freightExcludingCraneSurcharge != null) && (
                    <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 2, mb: sailing.freightCharges?.length > 0 ? 2.5 : 0 }}>
                      <Box>
                        <Typography variant="caption" color="text.secondary" display="block">
                          Total Freight & Charges (CI$)
                        </Typography>
                        <Typography fontWeight={600}>
                          {sailing.totalFreightAndChargesCi != null ? `$${sailing.totalFreightAndChargesCi.toFixed(2)}` : "—"}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary" display="block">
                          Cayman Crane Surcharge
                        </Typography>
                        <Typography fontWeight={600}>${(sailing.craneSurchargeAmount || 0).toFixed(2)}</Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary" display="block">
                          Freight Excl. Crane Surcharge
                        </Typography>
                        <Typography fontWeight={600}>
                          {sailing.freightExcludingCraneSurcharge != null ? `$${sailing.freightExcludingCraneSurcharge.toFixed(2)}` : "—"}
                        </Typography>
                      </Box>
                    </Box>
                  )}

                  {sailing.freightCharges?.length > 0 && (
                    <>
                      <Divider sx={{ mb: 1.5 }} />
                      <Typography variant="caption" color="text.secondary" display="block" mb={1}>
                        Itemized Charges
                      </Typography>
                      <Table size="small">
                        <TableBody>
                          {sailing.freightCharges.map((c, i) => (
                            <TableRow key={i}>
                              <TableCell sx={{ border: 0, py: 0.5, pl: 0 }}>{c.label}</TableCell>
                              <TableCell align="right" sx={{ border: 0, py: 0.5, pr: 0, fontFamily: "monospace" }}>
                                ${c.amount?.toFixed(2)}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        );
      })()}

      {/* Notes card */}
      {sailing.notes && (
        <Card>
          <CardHeader
            title={
              <Typography fontWeight={700} fontSize={14}>
                Notes
              </Typography>
            }
            sx={{ pb: 1 }}
          />
          <CardContent sx={{ pt: 0 }}>
            <Typography variant="body2" color="text.secondary">
              {sailing.notes}
            </Typography>
          </CardContent>
        </Card>
      )}

      {/* Shipments table */}
      <Card>
        <CardHeader
          title={
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Typography fontWeight={700} fontSize={14}>
                Assigned Items ({itemRows.length})
              </Typography>
            </Box>
          }
          action={
            <Box sx={{ display: "flex", gap: 1 }}>
              {itemRows.length > 0 && (
                <Button
                  size="small"
                  variant="outlined"
                  color="error"
                  startIcon={<Delete />}
                  onClick={() => setUnassignAllOpen(true)}
                >
                  Unassign All
                </Button>
              )}
              {canAssign && (
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<Add />}
                  onClick={() => setAssignOpen(true)}
                >
                  Assign More
                </Button>
              )}
            </Box>
          }
          sx={{ pb: 0 }}
        />

        {loading ? <SkeletonLoader rows={4} /> : null}

        {!loading && itemRows.length === 0 ? (
          <Box sx={{ textAlign: "center", py: 6 }}>
            <Box sx={{ fontSize: 40, mb: 1.5 }}>⛵</Box>
            <Typography fontWeight={700} mb={0.5}>
              No items assigned yet
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={2.5}>
              Assign received items to this sailing schedule.
            </Typography>
            {canAssign && (
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={() => setAssignOpen(true)}
              >
                Assign Items
              </Button>
            )}
          </Box>
        ) : (
          <Box sx={{ overflowX: "auto" }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Item</TableCell>
                  <TableCell>Item Tracking #</TableCell>
                  <TableCell>D/R</TableCell>
                  <TableCell>Container #</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Seal #</TableCell>
                  <TableCell align="right">Weight</TableCell>
                  <TableCell align="right">CBF</TableCell>
                  <TableCell align="right">Item Value</TableCell>
                  {canAssign && <TableCell align="center">Action</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {shipmentGroups.map((group) => (
                  <React.Fragment key={group.shipmentId}>
                    {/* Shipment header row */}
                    <TableRow
                      hover
                      sx={{
                        cursor: "pointer",
                        bgcolor: "#001A3C",
                        "&.MuiTableRow-hover:hover": { bgcolor: "#012452" },
                      }}
                      onClick={() => navigate(`/admin/shipments/${group.shipment.trackingNumber}`)}
                    >
                      <TableCell colSpan={canAssign ? 10 : 9} sx={{ borderBottom: "none" }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
                          <Typography
                            variant="body2"
                            fontWeight={800}
                            sx={{ fontFamily: "monospace", color: "white" }}
                          >
                            {group.shipment.trackingNumber}
                          </Typography>
                          <Typography variant="caption" fontWeight={700} sx={{ color: "rgba(255,255,255,0.75)" }}>
                            {group.shipment.customer?.firstName} {group.shipment.customer?.lastName}
                          </Typography>
                          <Chip
                            label={`${group.rows.length} item(s)`}
                            size="small"
                            sx={{
                              bgcolor: "rgba(255,255,255,0.15)",
                              color: "white",
                              fontWeight: 700,
                            }}
                          />
                        </Box>
                      </TableCell>
                    </TableRow>

                    {/* Item rows, nested under the shipment */}
                    {group.rows.map((row) => {
                      const { weight, cbf } = getItemMeasurements(row);
                      return (
                      <TableRow key={row.entryId}>
                        <TableCell sx={{ pl: 4 }}>
                          <Tooltip title={row.item?.description || ""} disableHoverListener={!row.item?.description}>
                            <Typography variant="body2" fontWeight={600} noWrap maxWidth={220}>
                              {truncateWords(row.item?.description) || "—"}
                            </Typography>
                          </Tooltip>
                          <Box sx={{ mt: 0.5 }}>
                            <StatusChip status={row.item?.itemStatus} domain="item" />
                          </Box>
                        </TableCell>
                        <TableCell
                          sx={{
                            fontFamily: "monospace",
                            fontSize: 12,
                            color: "text.secondary",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {row.item?.trackingNumber || "—"}
                        </TableCell>
                        <TableCell sx={{ fontFamily: "monospace", fontSize: 12, color: "text.secondary" }}>
                          {row.item?.dockReceiptNumber || "—"}
                        </TableCell>
                        <TableCell sx={{ fontFamily: "monospace", fontSize: 12, color: "text.secondary" }}>
                          {row.item?.containerNumber || "—"}
                        </TableCell>
                        <TableCell sx={{ fontFamily: "monospace", fontSize: 12, color: "text.secondary" }}>
                          {row.item?.containerType || "—"}
                        </TableCell>
                        <TableCell sx={{ fontFamily: "monospace", fontSize: 12, color: "text.secondary" }}>
                          {row.item?.sealNumber || "—"}
                        </TableCell>
                        <TableCell align="right" sx={{ fontSize: 13 }}>
                          {weight != null ? weight : "—"}
                        </TableCell>
                        <TableCell align="right" sx={{ fontSize: 13 }}>
                          {cbf != null ? cbf : "—"}
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>
                          {formatUsd(row.item?.totalValue)}
                        </TableCell>
                        {canAssign && (
                          <TableCell align="center">
                            <Tooltip title="Remove from sailing">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setUnassignTarget({
                                    shipmentId: row.shipmentId,
                                    itemId: row.itemId,
                                    trackingNumber: row.item?.trackingNumber,
                                  });
                                }}
                              >
                                <Delete fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        )}
                      </TableRow>
                      );
                    })}
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>

            {/* Totals footer */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "center",
                gap: 2,
                px: 2.5,
                py: 1.5,
                borderTop: "2px solid",
                borderColor: "divider",
                bgcolor: "action.hover",
              }}
            >
              <Typography variant="body2" color="text.secondary">
                {itemRows.length} item(s) · {shipmentGroups.length} shipment(s)
              </Typography>
              <Divider orientation="vertical" flexItem />
              <Typography variant="body2" fontWeight={700}>
                Total Value:{" "}
                <span style={{ color: "#0D629B" }}>
                  {formatUsd(totalValue)}
                </span>
              </Typography>
            </Box>
          </Box>
        )}
      </Card>

      {/* Assign Dialog */}
      <AssignShipmentsDialog
        open={assignOpen}
        sailing={sailing}
        onClose={() => setAssignOpen(false)}
        onAssigned={handleAssigned}
      />

      {/* Unassign Confirm Dialog */}
      <ConfirmDialog
        open={!!unassignTarget}
        title="Remove from Sailing?"
        message={`Remove item ${unassignTarget?.trackingNumber || ""} from ${sailing.sailingNumber}? It will be returned to Item Received status.`}
        confirmLabel="Remove"
        confirmColor="error"
        loading={unassigning}
        onConfirm={handleUnassign}
        onCancel={() => setUnassignTarget(null)}
      />

      {/* Unassign All Confirm Dialog */}
      <ConfirmDialog
        open={unassignAllOpen}
        title="Unassign All Items?"
        message={`Remove all ${itemRows.length} item(s) from ${sailing.sailingNumber}? They will all be returned to Item Received status.`}
        confirmLabel="Unassign All"
        confirmColor="error"
        loading={unassigningAll}
        onConfirm={handleUnassignAll}
        onCancel={() => setUnassignAllOpen(false)}
      />

      {/* Delete Sailing Confirm Dialog */}
      <ConfirmDialog
        open={deleteOpen}
        title="Delete Sailing?"
        message={`Delete sailing ${sailing.sailingNumber} (${sailing.vesselName})? This cannot be undone.${itemRows.length > 0 ? " Unassign all items first." : ""
          }`}
        confirmLabel="Delete"
        confirmColor="error"
        loading={deleting}
        onConfirm={handleDelete}
        onCancel={() => setDeleteOpen(false)}
      />
    </Box>
  );
};

export default AdminSailingDetail;
